import { ReactElement } from 'react'

import keys from 'lodash/keys'
import lowerCase from 'lodash/lowerCase'
import { Link } from 'react-router-dom'

import { AsinLink } from 'components/Links'
import { UNDEFINED_VALUE } from 'const/formatting'
import {
    AUTOMATION_TASK,
    CAMPAIGN,
    KEYWORD,
    PRODUCT_AD,
    TARGET,
    NEGATIVE_KEYWORD,
} from 'const/resourceTypes'
import AutomationTypeContainer from 'features/Automations/components/AutomationType/Automation/AutomationTypeContainer'
import { isASIN } from 'helpers/asin'
import { CHANGE_TERM_VALUE_FORMATTERS } from 'helpers/changelog'
import { titleCase } from 'helpers/formatting'
import { isUnset } from 'helpers/utilities'
import { changelogResourceUrl } from 'routing/urls'
import { ChangelogRecord, CellRenderProps } from 'types'

import * as styles from './styles.scss'

type RenderProps<RecordType> = {
    cellRenderProps: CellRenderProps<RecordType>
    dataIndex?: string[]
}

const KNOWN_TARGET_MATCH_TYPES: { [key: string]: string } = {
    SEARCH_LOOSE_MATCH: 'Auto Targeting: Loose Match',
    SEARCH_CLOSE_MATCH: 'Auto Targeting: Close Match',
    PRODUCT_SUBSTITUTES: 'Auto Targeting: Substitutes',
    PRODUCT_COMPLEMENTS: 'Auto Targeting: Product Complements',
    PRODUCT_SIMILAR: 'Similar Product',
    SEARCH_RELATED_TO_YOUR_BRAND: 'Auto Targeting: Related to Your Brand',
    SEARCH_RELATED_TO_YOUR_LANDING_PAGES:
        'Auto Targeting: Related to Your Landing Pages',
    BROAD: 'Broad Match',
    PHRASE: 'Phrase Match',
    EXACT: 'Exact Match',
    PRODUCT_EXACT: 'Exact Product',
    UNKNOWN: 'Unknown',
    UNEXPECTED_VALUE: 'Unknown',
}

function changelogTargetObjectNameRenderer<RecordType extends ChangelogRecord>({
    cellRenderProps: { value, record },
}: RenderProps<RecordType>): ReactElement {
    if (isUnset(value)) {
        return <span>{UNDEFINED_VALUE}</span>
    }

    if (value.startsWith('{')) {
        // likely a json from target details
        let json = value
        if (!value.endsWith('}')) {
            json = `${value.slice(0, value.lastIndexOf(','))}}`
        }
        try {
            const parsedValue = JSON.parse(json)
            let index = 0
            return (
                <span>
                    {Object.keys(parsedValue).map((key: string) => {
                        const part = parsedValue[key]
                        if (part === null) {
                            return null
                        }
                        index += 1
                        const presentableKey = titleCase(key)
                        let presentableValue = (
                            <span className={styles['old-value']}>{part}</span>
                        )
                        if (isASIN(part) && key === 'asin') {
                            presentableValue = (
                                <AsinLink
                                    asin={part}
                                    countryCode={
                                        record?.campaign?.profile?.country_code
                                    }
                                />
                            )
                        }
                        if (key === 'match_type') {
                            const matchType =
                                KNOWN_TARGET_MATCH_TYPES[part] ?? part
                            presentableValue = (
                                <span className={styles['old-value']}>
                                    {matchType}
                                </span>
                            )
                        }
                        return (
                            <span key={key}>
                                {index > 1 ? ', ' : ''}
                                {presentableKey}: {presentableValue}
                            </span>
                        )
                    })}
                </span>
            )
        } catch (_e) {
            return <span>{value}</span>
        }
    } else {
        const desciptionParts = value.split(' ')
        return (
            <span>
                {desciptionParts.map((part: string) => {
                    if (isASIN(part)) {
                        return (
                            <AsinLink
                                key={part}
                                asin={part}
                                countryCode={
                                    record?.campaign?.profile?.country_code
                                }
                            />
                        )
                    }
                    return <span key={part}>{part} </span>
                })}
            </span>
        )
    }
}

function changelogObjectNameWithAutomationProfileRenderer<
    RecordType extends ChangelogRecord,
>({
    cellRenderProps: { value, record },
}: RenderProps<RecordType>): ReactElement {
    if (isUnset(value)) {
        return <span>{UNDEFINED_VALUE}</span>
    }

    const resourceLabel =
        record.object_type === AUTOMATION_TASK
            ? record.automation_profile?.name
            : value
    return record.automation_profile ? (
        <span style={{ display: 'flex', gap: '0.5rem' }}>
            <Link
                to={changelogResourceUrl(
                    record.object_type,
                    record.automation_profile.id,
                    record.campaign
                )}
            >
                {resourceLabel ?? value}
            </Link>
            {' - '}
            <AutomationTypeContainer
                capabilityId={record.automation_profile.capability_id}
            />
        </span>
    ) : (
        <span>{value}</span>
    )
}

export function changelogObjectNameRenderer<RecordType extends ChangelogRecord>(
    props: RenderProps<RecordType>
): ReactElement {
    const linkableResourceTypes = [
        CAMPAIGN,
        KEYWORD,
        PRODUCT_AD,
        AUTOMATION_TASK,
    ]

    const { value, record } = props.cellRenderProps

    if (isUnset(value)) {
        return <span>{UNDEFINED_VALUE}</span>
    }

    if (record.object_type === AUTOMATION_TASK) {
        return changelogObjectNameWithAutomationProfileRenderer(props)
    }

    if (
        record.object_type === TARGET ||
        record.object_type === NEGATIVE_KEYWORD
    ) {
        return changelogTargetObjectNameRenderer(props)
    }

    return linkableResourceTypes.includes(record.object_type) ? (
        <Link
            to={changelogResourceUrl(
                record.object_type,
                record.object_id,
                record.campaign
            )}
        >
            {value}
        </Link>
    ) : (
        <span>{value}</span>
    )
}

interface ChangelogValueProps {
    changedProp: string
    oldValue: string | number | boolean | null
    newValue: string | number | boolean | null
}

const CHANGE_TERM_FORMATTERS: {
    [changedProp: string]: (props: ChangelogValueProps) => ReactElement | null
} = {
    paused: ({ oldValue, newValue }: ChangelogValueProps): ReactElement => {
        const formattedOldValue = oldValue ? 'Paused' : 'Active'
        const formattedNewValue = newValue ? 'Paused' : 'Active'
        return (
            <>
                <span className={styles['changed-field']}>Status</span>:{' '}
                <span className={styles['old-value']}>{formattedOldValue}</span>{' '}
                →{' '}
                <span className={styles['new-value']}>{formattedNewValue}</span>
            </>
        )
    },
}

function ChangelogValue(props: ChangelogValueProps): ReactElement | null {
    const { changedProp, oldValue, newValue } = props

    if (changedProp in CHANGE_TERM_FORMATTERS) {
        return CHANGE_TERM_FORMATTERS[changedProp](props)
    }

    function formatValue(
        fieldName: string,
        value: string | number | boolean | object | null
    ): string {
        if (isUnset(value)) {
            return 'None'
        }
        const formatter = (CHANGE_TERM_VALUE_FORMATTERS as any)[fieldName]
        if (formatter) {
            return formatter(value, value)
        }
        if (typeof value === 'object') {
            if (
                value &&
                ('items' in value ||
                    'added_items' in value ||
                    'removed_items' in value) &&
                'count' in value
            ) {
                if (value.count === 0) {
                    return 'None'
                }
                const formattedValue = []
                if (
                    'added_items' in value &&
                    Array.isArray(value.added_items) &&
                    value.added_items.length > 0
                ) {
                    formattedValue.push(
                        `Added: [${(value.added_items ?? [])
                            .map(
                                (item: any) =>
                                    item.label ?? item.value ?? `${item}`
                            )
                            .join(', ')}]`
                    )
                }
                if (
                    'removed_items' in value &&
                    Array.isArray(value.removed_items) &&
                    value.removed_items.length > 0
                ) {
                    formattedValue.push(
                        `Removed: [${(value.removed_items ?? [])
                            .map(
                                (item: any) =>
                                    item.label ?? item.value ?? `${item}`
                            )
                            .join(', ')}]`
                    )
                }
                if (
                    !formattedValue.length &&
                    'items' in value &&
                    Array.isArray(value.items)
                ) {
                    formattedValue.push(
                        `[${(value.items ?? [])
                            .map(
                                (item: any) =>
                                    item.label ?? item.value ?? `${item}`
                            )
                            .join(', ')}]`
                    )
                }
                return formattedValue.join(', ')
            }
            return JSON.stringify(value)
        }
        return `${value}`
    }

    function formatKey(key: string): string {
        let formattedKey = key ?? ''
        if (formattedKey.endsWith('_int')) {
            formattedKey = formattedKey.replace('_int', '')
        }
        return lowerCase(formattedKey)
    }

    const formattedOldValue = formatValue(changedProp, oldValue)
    const formattedNewValue = formatValue(changedProp, newValue)

    if (formattedOldValue === formattedNewValue) {
        return null
    }

    return (
        <>
            <span className={styles['changed-field']}>
                {formatKey(changedProp)}
            </span>
            : <span className={styles['old-value']}>{formattedOldValue}</span> →{' '}
            <span className={styles['new-value']}>{formattedNewValue}</span>
        </>
    )
}

export function changelogChangesRenderer<RecordType extends ChangelogRecord>({
    cellRenderProps: { value, record },
}: RenderProps<RecordType>): ReactElement {
    if (isUnset(value) && isUnset(record)) {
        return <span>{UNDEFINED_VALUE}</span>
    }

    const {
        change_type: historyType,
        object_type: objectType,
        changes,
    } = record
    const resourceTypeLabel = lowerCase(objectType)

    if (historyType === '+') {
        return <span>{`Created new ${resourceTypeLabel}`}</span>
    }
    if (historyType === '-') {
        return <span>Deleted</span>
    }

    const changeList = []
    const allKeys = keys(changes)
    for (let i = 0; i < allKeys.length; i += 1) {
        const key = allKeys[i]
        const change = changes[key]
        if (change) {
            changeList.push(
                <div key={key}>
                    <ChangelogValue
                        changedProp={key}
                        oldValue={change.old}
                        newValue={change.new}
                    />
                </div>
            )
        }
    }

    return <div>{changeList.map((change) => change)}</div>
}
